// `__sendStatus`, `__pending`, `__error` are intentional client-side
// namespace markers added by the optimistic sendAdminComment thunk.
// The double-underscore prefix distinguishes them from server-returned
// comment fields so we can never collide with anything from the API.
/* eslint-disable no-underscore-dangle */
import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
} from "@material-ui/core";
import {
  AttachFile as AttachFileIcon,
  Schedule as ScheduleIcon,
  Done as DoneIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@material-ui/icons";
import moment from "moment";
import InternalNoteTag from "../internal-note-tag";
import AttachmentPreviewDialog from "../attachment-preview-dialog";

// SmartKood brand tokens, mgmt portal scope. Match the OKLCH palette
// declared in repo-root .impeccable.md (slate primary, brand green
// accent, neutrals tinted toward slate hue 218).
const TOKENS = {
  SLATE_700: "#3A4D54", // brand slate, primary text + own avatar bg
  SLATE_500: "#5C6B70", // muted text
  SLATE_400: "#7E8B8F", // tertiary text
  SLATE_300: "#C4CCD0", // hairline border (use with /80 alpha for whisper)
  SLATE_100: "#EFF1F2", // subtle surface tint
  SURFACE_0: "#FCFCFC", // page bg
  SURFACE_1: "#F6F7F8", // alt surface (customer/external bubble)
  GREEN_500: "#6AAF68", // brand accent
  AMBER_FG: "#8A6B1F", // calmer than #D97706 for "internal" indicator
  AMBER_BG: "#FBF3DC", // warm tinted surface for internal-note bubble
};
// Mono stack for timestamps + IDs so columns line up across messages.
// System mono — no network font load needed (per design context).
const MONO_STACK = `ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace`;

const CommentItem = ({ comment, onRetry }) => {
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const isInternal = comment.is_internal;
  const isAgent = comment.user_type === "agent";

  const initials = comment.user_name
    ? comment.user_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  // Three visual treatments, no side-stripes (banned in .impeccable.md).
  // Differentiation comes from background tint + a single hairline
  // border, not from a colored 3px accent rail.
  let avatarBg;
  let avatarColor;
  let contentBg;
  let contentBorder;
  if (isInternal) {
    avatarBg = TOKENS.AMBER_BG;
    avatarColor = TOKENS.AMBER_FG;
    contentBg = TOKENS.AMBER_BG;
    contentBorder = `${TOKENS.AMBER_FG}33`;
  } else if (isAgent) {
    avatarBg = TOKENS.SLATE_700;
    avatarColor = "#fff";
    contentBg = "#fff";
    contentBorder = `${TOKENS.SLATE_300}80`;
  } else {
    avatarBg = TOKENS.SLATE_100;
    avatarColor = TOKENS.SLATE_700;
    contentBg = TOKENS.SURFACE_1;
    contentBorder = `${TOKENS.SLATE_300}66`;
  }

  // Conversation alignment, agent's perspective:
  //   - Customer messages    -> LEFT  (incoming)
  //   - Agent messages       -> RIGHT (outgoing — your team)
  //   - Internal notes       -> RIGHT (still agent-authored, but the
  //                                    amber palette + InternalNoteTag
  //                                    flag that the customer can't
  //                                    see this)
  // Avatar always sits on the OUTSIDE edge of the bubble (right side
  // of an agent bubble, left side of a customer bubble) so the whole
  // row reads as one chat-style unit.
  const isRightSide = isAgent; // covers regular agent + internal notes

  // Send-status indicator — agent bubbles always get one (sending /
  // sent / failed). Customer bubbles never do. Returns null on the
  // customer side so we can collapse the timestamp row gracefully.
  const renderStatus = () => {
    if (!isAgent) return null;
    const status = comment.__sendStatus || "sent";
    if (status === "sending") {
      return (
        <Tooltip title="Sending…" arrow>
          <ScheduleIcon style={{ fontSize: 14, color: TOKENS.SLATE_400 }} />
        </Tooltip>
      );
    }
    if (status === "sent") {
      return (
        <Tooltip title="Sent" arrow>
          <DoneIcon style={{ fontSize: 14, color: TOKENS.GREEN_500 }} />
        </Tooltip>
      );
    }
    if (status === "failed") {
      return (
        <>
          <ErrorOutlineIcon style={{ fontSize: 14, color: "#B53A3A" }} />
          <Typography
            sx={{
              fontFamily: MONO_STACK,
              fontSize: 11,
              color: "#B53A3A",
              fontWeight: 500,
              letterSpacing: "0.01em",
            }}
          >
            {comment.__error || "Failed"}
          </Typography>
          <Tooltip title="Retry" arrow>
            <IconButton
              size="small"
              aria-label="Retry sending message"
              onClick={() => onRetry && onRetry(comment)}
              sx={{
                p: 0.25,
                color: "#B53A3A",
                "&:hover": { backgroundColor: "rgba(181, 58, 58, 0.08)" },
              }}
            >
              <RefreshIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        mb: "14px",
        justifyContent: isRightSide ? "flex-end" : "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isRightSide ? "row-reverse" : "row",
          alignItems: "flex-start",
          gap: "10px",
          maxWidth: "78%",
          minWidth: 0,
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.02em",
            borderRadius: "8px",
            backgroundColor: avatarBg,
            color: avatarColor,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>

        {/* Content column — sits to the right of the avatar (or left
            when isRightSide flips the parent's row-reverse). All inner
            rows align to the same edge as the bubble. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isRightSide ? "flex-end" : "flex-start",
            minWidth: 0,
            flex: "0 1 auto",
          }}
        >
          {/* Author / role / internal-note tag — sits ABOVE the bubble */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: isRightSide ? "row-reverse" : "row",
              gap: "8px",
              mb: "4px",
              maxWidth: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: TOKENS.SLATE_700,
                lineHeight: 1.2,
              }}
            >
              {comment.user_name}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: TOKENS.SLATE_500,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {isAgent ? "Agent" : "Customer"}
            </Typography>
            {isInternal && <InternalNoteTag />}
          </Box>

          {/* Content card. The borderLeft 3px side-stripe pattern is on
              the absolute-bans list in .impeccable.md, so differentiation
              between customer / agent / internal-note now comes from
              background tint + a single hairline border on all sides. */}
          <Box
            sx={{
              backgroundColor: contentBg,
              border: `1px solid ${contentBorder}`,
              borderRadius: "6px",
              padding: "10px 14px",
              fontSize: 14,
              lineHeight: 1.55,
              color: TOKENS.SLATE_700,
              maxWidth: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "inherit",
              }}
            >
              {comment.content}
            </Typography>
          </Box>

          {comment.attachments && comment.attachments.length > 0 && (
            <Box
              sx={{
                display: "flex",
                gap: "6px",
                mt: "6px",
                flexWrap: "wrap",
                justifyContent: isRightSide ? "flex-end" : "flex-start",
              }}
            >
              {comment.attachments.map((att) => (
                <Box
                  key={att.id}
                  onClick={() => setPreviewAttachment(att)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 9px",
                    backgroundColor: "#fff",
                    border: `1px solid ${TOKENS.SLATE_300}80`,
                    borderRadius: "4px",
                    fontSize: 12,
                    color: TOKENS.SLATE_700,
                    cursor: "pointer",
                    transition: "border-color 0.18s, background-color 0.18s",
                    "&:hover": {
                      borderColor: TOKENS.SLATE_700,
                      backgroundColor: TOKENS.SLATE_100,
                    },
                  }}
                >
                  <AttachFileIcon
                    style={{ fontSize: 14, color: TOKENS.SLATE_500 }}
                  />
                  {att.file_name}
                </Box>
              ))}
            </Box>
          )}

          {/* Timestamp + send-status — sits BELOW the bubble (and any
              attachments) on the same edge as the bubble. WhatsApp-style:
              compact, mono, tabular-nums so columns line up across the
              thread. */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              flexDirection: isRightSide ? "row-reverse" : "row",
              gap: 0.75,
              mt: "4px",
              maxWidth: "100%",
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO_STACK,
                fontSize: 11,
                color: TOKENS.SLATE_500,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "0.01em",
              }}
            >
              {moment(comment.created_at).format("MMM D · h:mm A")}
            </Typography>
            {renderStatus()}
          </Box>
        </Box>
      </Box>

      <AttachmentPreviewDialog
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        fileName={previewAttachment?.file_name}
        fileType={previewAttachment?.file_type}
        downloadUrl={previewAttachment?.download_url}
      />
    </Box>
  );
};

CommentItem.propTypes = {
  comment: PropTypes.object.isRequired,
  onRetry: PropTypes.func,
};

export default CommentItem;
